import React from 'react';
import { FormGroup, Label, Input, Row, Col, FormFeedback } from 'reactstrap';
import DatePicker from 'react-datepicker';

interface FormData {
  name: string;
  dateOfIncident: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  descriptionOfFeedback: string;
  immediateFeedback: string;
  expectationForImprovement: string;
  emailOfFeedback: string;
}

interface FormErrors {
  name?: string;
  dateOfIncident?: string;
  policyViolationIds?: string;
  descriptionOfFeedback?: string;
  expectationForImprovement?: string;
}

interface PolicyViolation {
  id: string;
  name: string;
}

interface UserInfoSectionProps {
  formData: FormData;
  errors: FormErrors;
  onChange: (field: keyof FormData, value: string) => void;
}

export const UserInfoSection = ({ formData, errors, onChange }: UserInfoSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">User Information</h5>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="name">
            Name <span className="text-danger">*</span>
          </Label>
          <Input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            invalid={!!errors.name}
            readOnly
            disabled
          />
          {errors.name && <FormFeedback>{errors.name}</FormFeedback>}
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="dateOfIncident">
            Date of Incident <span className="text-danger">*</span>
          </Label>
          <Input
            tag={DatePicker}
            dateFormat="MM/dd/yyyy"
            id="dateOfIncident"
            selected={formData.dateOfIncident ? new Date(formData.dateOfIncident + 'T00:00:00') : null}
            onChange={(d: any) => onChange('dateOfIncident', d ? d.toISOString().split('T')[0] : '')}
            maxDate={new Date()}
            placeholderText="Select date"
            autoComplete="off"
          />
          {errors.dateOfIncident && <FormFeedback>{errors.dateOfIncident}</FormFeedback>}
        </FormGroup>
      </Col>
    </Row>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="position">Position</Label>
          <Input
            type="text"
            id="position"
            value={formData.position}
            onChange={(e) => onChange('position', e.target.value)}
            readOnly
            disabled
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="department">Department</Label>
          <Input
            type="text"
            id="department"
            value={formData.department}
            onChange={(e) => onChange('department', e.target.value)}
            readOnly
            disabled
          />
        </FormGroup>
      </Col>
    </Row>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="supervisor">Supervisor</Label>
          <Input
            type="text"
            id="supervisor"
            value={formData.supervisor}
            readOnly
            onChange={(e) => onChange('supervisor', e.target.value)}
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="dateOfHire">Date of Hire (Read-only)</Label>
          <Input type="date" id="dateOfHire" value={formData.dateOfHire} readOnly disabled />
        </FormGroup>
      </Col>
    </Row>
  </div>
);

interface PolicySectionProps {
  formData: FormData;
  errors: FormErrors;
  policyViolations: PolicyViolation[];
  onChange: (field: keyof FormData, value: string | string[]) => void;
}

export const PolicySection = ({ formData, errors, policyViolations, onChange }: PolicySectionProps) => {
  const handleCheckboxChange = (violationId: string) => {
    const newIds = formData.policyViolationIds.includes(violationId)
      ? formData.policyViolationIds.filter((id) => id !== violationId)
      : [...formData.policyViolationIds, violationId];
    onChange('policyViolationIds', newIds);
  };

  return (
    <div className="mb-4">
      <h5 className="text-primary mb-3">Policy Violations</h5>
      <FormGroup>
        <Label>
          Policy Violations <span className="text-danger">*</span>
        </Label>
        <div>
          {policyViolations.map((violation) => (
            <FormGroup check key={violation.id} className="mb-2">
              <Label check>
                <Input
                  type="checkbox"
                  checked={formData.policyViolationIds.includes(violation.id)}
                  onChange={() => handleCheckboxChange(violation.id)}
                />
                {violation.name}
              </Label>
            </FormGroup>
          ))}
        </div>
        {errors.policyViolationIds && (
          <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
            {errors.policyViolationIds}
          </div>
        )}
      </FormGroup>
    </div>
  );
};

interface FeedbackDescriptionSectionProps {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

export const FeedbackDescriptionSection = ({ value, error, onChange }: FeedbackDescriptionSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Description of Feedback</h5>
    <FormGroup>
      <Label for="descriptionOfFeedback">
        Description of Feedback <span className="text-danger">*</span>
      </Label>
      <Input
        type="textarea"
        id="descriptionOfFeedback"
        rows={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        invalid={!!error}
        placeholder="Provide a detailed description of the feedback..."
      />
      {error && <FormFeedback>{error}</FormFeedback>}
    </FormGroup>
  </div>
);

interface RadioSectionProps {
  title: string;
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
}

export const RadioSection = ({ title, label, name, value, onChange }: RadioSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">{title}</h5>
    <FormGroup>
      <Label>{label}</Label>
      <div>
        <FormGroup check inline>
          <Label check>
            <Input
              type="radio"
              name={name}
              value="yes"
              checked={value === 'yes'}
              onChange={(e) => onChange(e.target.value)}
            />
            Yes
          </Label>
        </FormGroup>
        <FormGroup check inline>
          <Label check>
            <Input
              type="radio"
              name={name}
              value="no"
              checked={value === 'no'}
              onChange={(e) => onChange(e.target.value)}
            />
            No
          </Label>
        </FormGroup>
      </div>
    </FormGroup>
  </div>
);

interface ExpectationSectionProps {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

export const ExpectationSection = ({ value, error, onChange }: ExpectationSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Expectation for Improvement</h5>
    <FormGroup>
      <Label for="expectationForImprovement">
        Expectation for Improvement <span className="text-danger">*</span>
      </Label>
      <Input
        type="textarea"
        id="expectationForImprovement"
        rows={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        invalid={!!error}
        placeholder="Describe the expectations for improvement..."
      />
      {error && <FormFeedback>{error}</FormFeedback>}
    </FormGroup>
  </div>
);
